import { 
  getElementDetail,
  getAllElementsOrderedDesc,
  getElementsForFirstPage,
  getElementsForOtherPages,
  getLastArticles,
  getAllElementsNumber
} from '../../data/FirebaseService.js';
import { ElementType } from '../../data/ElementType.js';

/**
 * Repository for managing all Article-related data
 * Provides methods to fetch articles, article details, and related articles
 */
class ArticleRepository {
  
  /**
   * Fetch a specific article by ID
   * @param {number} id - Article ID
   * @returns {Promise<Object>} Article object
   */
  async getArticleById(id) {
    try {
      const articleId = parseInt(id);
      if (isNaN(articleId)) {
        throw new Error('Invalid article ID');
      }

      const article = await getElementDetail(ElementType.ArticleType, articleId);
      
      if (!article) {
        throw new Error('Article not found');
      }

      return article;
    } catch (error) {
      console.error(`Error fetching article ${id}:`, error);
      throw new Error('Failed to fetch article');
    }
  }

  /**
   * Resolve an author id into a display name.
   * Authors are modeled as members (role "Autore") in the "members" collection.
   * Never throws: returns null when the author is missing or cannot be resolved,
   * so a failed lookup does not break article loading.
   * @param {number|string} authorId - Author id referencing a member
   * @returns {Promise<string|null>} Author display name ("Name Surname") or null
   */
  async getAuthorName(authorId) {
    try {
      if (authorId === null || authorId === undefined) {
        return null;
      }

      const member = await getElementDetail(ElementType.MemberType, parseInt(authorId));
      if (!member) {
        return null;
      }

      const name = [member.name, member.surname].filter(Boolean).join(' ').trim();
      return name || null;
    } catch (error) {
      console.error(`Error fetching author ${authorId}:`, error);
      return null;
    }
  }

  /**
   * Fetch all articles ordered by most recent
   * @returns {Promise<Array>} Array of article objects
   */
  async getAllArticles() {
    try {
      const articles = await getAllElementsOrderedDesc(ElementType.ArticleType);
      return articles || [];
    } catch (error) {
      console.error('Error fetching all articles:', error);
      throw new Error('Failed to fetch articles');
    }
  }

  /**
   * Fetch the latest N articles
   * @param {number} limit - Number of articles to fetch (default: 4)
   * @returns {Promise<Array>} Array of recent article objects
   */
  async getLatestArticles(limit = 4) {
    try {
      if (limit === 4) {
        // Use the optimized Firebase function for 4 articles
        const articles = await getLastArticles();
        return articles || [];
      } else {
        // Fetch all and slice
        const allArticles = await getAllElementsOrderedDesc(ElementType.ArticleType);
        return allArticles ? allArticles.slice(0, limit) : [];
      }
    } catch (error) {
      console.error('Error fetching latest articles:', error);
      throw new Error('Failed to fetch latest articles');
    }
  }

  /**
   * Fetch articles for the first page (paginated)
   * @returns {Promise<Array>} Array of article objects (max 12)
   */
  async getArticlesFirstPage() {
    try {
      const articles = await getElementsForFirstPage(ElementType.ArticleType);
      return articles || [];
    } catch (error) {
      console.error('Error fetching first page articles:', error);
      throw new Error('Failed to fetch articles');
    }
  }

  /**
   * Fetch articles for a specific page (paginated)
   * @param {number} pageNumber - Page number (0-indexed)
   * @returns {Promise<Array>} Array of article objects (max 12)
   */
  async getArticlesForPage(pageNumber) {
    try {
      if (pageNumber === 0) {
        return await this.getArticlesFirstPage();
      }

      const totalArticles = await this.getTotalArticlesCount();
      const articles = await getElementsForOtherPages(
        ElementType.ArticleType,
        totalArticles,
        pageNumber
      );
      
      return articles || [];
    } catch (error) {
      console.error(`Error fetching page ${pageNumber} articles:`, error);
      throw new Error('Failed to fetch articles');
    }
  }

  /**
   * Get total number of articles
   * @returns {Promise<number>} Total article count
   */
  async getTotalArticlesCount() {
    try {
      const count = await getAllElementsNumber(ElementType.ArticleType.elementName);
      return count || 0;
    } catch (error) {
      console.error('Error fetching article count:', error);
      throw new Error('Failed to fetch article count');
    }
  }

  /**
   * Fetch related articles (excluding current article)
   * @param {number} currentArticleId - Current article ID to exclude
   * @param {number} limit - Number of related articles to fetch (default: 3)
   * @returns {Promise<Array>} Array of related article objects
   */
  async getRelatedArticles(currentArticleId, limit = 3) {
    try {
      // Fetch latest articles and filter out the current one
      const articles = await this.getLatestArticles(limit + 5);
      
      const relatedArticles = articles
        .filter(article => article.id !== parseInt(currentArticleId))
        .slice(0, limit);

      return relatedArticles;
    } catch (error) {
      console.error('Error fetching related articles:', error);
      throw new Error('Failed to fetch related articles');
    }
  }

  /**
   * Fetch article with related articles in one call
   * @param {number} id - Article ID
   * @param {number} relatedLimit - Number of related articles (default: 3)
   * @returns {Promise<Object>} Object containing article and related articles
   */
  async getArticleWithRelated(id, relatedLimit = 3) {
    try {
      const [article, relatedArticles] = await Promise.all([
        this.getArticleById(id),
        this.getRelatedArticles(id, relatedLimit)
      ]);

      const author = await this.getAuthorName(article?.author_id);

      return {
        article,
        relatedArticles,
        author
      };
    } catch (error) {
      console.error(`Error fetching article ${id} with related:`, error);
      throw new Error('Failed to fetch article data');
    }
  }

  /**
   * Search articles by title
   * @param {string} searchTerm - Search term
   * @returns {Promise<Array>} Array of matching articles
   */
  async searchArticles(searchTerm) {
    try {
      if (!searchTerm || searchTerm.trim() === '') {
        return [];
      }

      const allArticles = await this.getAllArticles();
      const lowerSearchTerm = searchTerm.toLowerCase();

      return allArticles.filter(article => 
        article.title?.toLowerCase().includes(lowerSearchTerm) ||
        article.text?.toLowerCase().includes(lowerSearchTerm)
      );
    } catch (error) {
      console.error('Error searching articles:', error);
      throw new Error('Failed to search articles');
    }
  }

  /**
   * Filter articles by date range
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Promise<Array>} Array of articles within date range
   */
  async getArticlesByDateRange(startDate, endDate) {
    try {
      const allArticles = await this.getAllArticles();
      
      return allArticles.filter(article => {
        const articleDate = new Date(article.date);
        return articleDate >= startDate && articleDate <= endDate;
      });
    } catch (error) {
      console.error('Error filtering articles by date:', error);
      throw new Error('Failed to filter articles');
    }
  }

  /**
   * Get article statistics
   * @returns {Promise<Object>} Statistics about articles
   */
  async getArticleStats() {
    try {
      const [totalCount, allArticles] = await Promise.all([
        this.getTotalArticlesCount(),
        this.getAllArticles()
      ]);

      // Calculate stats
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      const recentArticles = allArticles.filter(article => {
        const articleDate = new Date(article.date);
        return articleDate >= thirtyDaysAgo;
      });

      // Group by month
      const byMonth = {};
      allArticles.forEach(article => {
        const date = new Date(article.date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        byMonth[monthKey] = (byMonth[monthKey] || 0) + 1;
      });

      return {
        total: totalCount,
        recentCount: recentArticles.length,
        byMonth,
        latestArticle: allArticles[0] || null
      };
    } catch (error) {
      console.error('Error fetching article stats:', error);
      throw new Error('Failed to fetch article statistics');
    }
  }

  /**
   * Get pagination info
   * @param {number} currentPage - Current page number (0-indexed)
   * @param {number} pageSize - Items per page (default: 10)
   * @returns {Promise<Object>} Pagination information
   */
  async getPaginationInfo(currentPage = 0, pageSize = 10) {
    try {
      const totalCount = await this.getTotalArticlesCount();
      const totalPages = Math.ceil(totalCount / pageSize);
      
      return {
        currentPage,
        totalPages,
        totalItems: totalCount,
        pageSize,
        hasNextPage: currentPage < totalPages - 1,
        hasPreviousPage: currentPage > 0
      };
    } catch (error) {
      console.error('Error calculating pagination:', error);
      throw new Error('Failed to calculate pagination');
    }
  }
}

// Export singleton instance
const articleRepository = new ArticleRepository();
export default articleRepository;
